/**
 * Project domain constants — value-side complement to `model/types.ts`.
 *
 * Migrated from `features/projects/constants/projects.constants.ts`. The only
 * intentional change in this commit is **retyping `PROJECT_STATUS_MAP`'s color
 * field as `ProjectStatusVariant`** instead of v3 monks-ui `ChipColor`.
 *
 * v3 ChipColor → ProjectStatusVariant mapping (for future maintainers):
 *   Grey → neutral
 *   Orange → warning
 *   lightBlue → info
 *   Green → success
 *   Red → destructive
 *
 * The labels themselves are unchanged so existing snapshot tests pass without
 * --update. The Shadcn `Badge` swap will read the
 * `variant` field — not the `label` — to decide which DS variant to render.
 */
import {
  CalendarDaysIcon,
  LightbulbIcon,
  MegaphoneIcon,
  PuzzleIcon,
  SmileIcon,
  ZapIcon,
  type LucideIcon,
} from 'lucide-react';
import { i18n } from '@/shared/i18n';
import type { Column } from '@/shared/ui';
import type {
  ProjectListItem,
  ProjectSectionKey,
  ProjectSortKey,
  ProjectStatus,
  ProjectStatusVariant,
  ProjectType,
} from './types';

/**
 * Display labels for the project detail sections (matches the sidebar tabs).
 *
 * Reconciled from `features/projects/constants/projects.constants.ts`. Lives
 * in the entity layer for the same reason `ProjectSectionKey` does — both the
 * sidebar (feature) and the page route titles (pages) need it.
 */
// Labels here and in the maps below are getters so each read resolves against
// the active locale; a plain `i18n.t(...)` would freeze English at import time.
export const PROJECT_SECTION_LABELS = {
  get overview() {
    return i18n.t('projects:project.constants.overview');
  },
  get briefing() {
    return i18n.t('projects:project.constants.briefs');
  },
  get library() {
    return i18n.t('projects:project.constants.library');
  },
  get folders() {
    return i18n.t('projects:project.constants.approval');
  },
  get dashboard() {
    return i18n.t('projects:project.constants.dashboard');
  },
  get delivery() {
    return i18n.t('projects:project.constants.delivery');
  },
  get performance() {
    return i18n.t('projects:project.constants.performance');
  },
} as const satisfies Record<ProjectSectionKey, string>;

export const PROJECT_TYPE_MAP = {
  get brand_strategy() {
    return i18n.t('projects:project.constants.brandStrategy');
  },
  get campaigns() {
    return i18n.t('projects:project.constants.campaigns');
  },
  get creative_production() {
    return i18n.t('projects:project.constants.creativeProduction');
  },
  get content_marketing() {
    return i18n.t('projects:project.constants.contentMarketing');
  },
  get events() {
    return i18n.t('projects:project.constants.events');
  },
  get experiential_marketing() {
    return i18n.t('projects:project.constants.experientialMarketing');
  },
} as const satisfies Record<ProjectType, string>;

/**
 * Canonical icon lookup for each `ProjectType`. Consumed by the projects
 * table type chip + the toolbar type filter so both stay in sync.
 */
export const PROJECT_TYPE_ICONS = {
  brand_strategy: PuzzleIcon,
  campaigns: MegaphoneIcon,
  creative_production: LightbulbIcon,
  content_marketing: ZapIcon,
  events: CalendarDaysIcon,
  experiential_marketing: SmileIcon,
} as const satisfies Record<ProjectType, LucideIcon>;

interface ProjectSortChoice {
  id: string;
  label: string;
  sortBy: ProjectSortKey;
  sortOrder: 'ASC' | 'DESC';
  direction: 'asc' | 'desc';
}

/**
 * Sort options surfaced in the Projects list toolbar. `last_activity` is the
 * default because it answers "what did I touch most recently" — the same
 * question the Last Activity column visualises.
 */
export const PROJECT_SORT_CHOICES = [
  {
    id: 'created_at-asc',
    get label() {
      return i18n.t('projects:project.constants.createdOldestFirst');
    },
    sortBy: 'created_at',
    sortOrder: 'ASC',
    direction: 'asc',
  },
  {
    id: 'last_activity-desc',
    get label() {
      return i18n.t('projects:project.constants.lastActivityRecentFirst');
    },
    sortBy: 'last_activity',
    sortOrder: 'DESC',
    direction: 'desc',
  },
  {
    id: 'last_activity-asc',
    get label() {
      return i18n.t('projects:project.constants.lastActivityOldestFirst');
    },
    sortBy: 'last_activity',
    sortOrder: 'ASC',
    direction: 'asc',
  },
  {
    id: 'title-asc',
    get label() {
      return i18n.t('projects:project.constants.nameAZ');
    },
    sortBy: 'title',
    sortOrder: 'ASC',
    direction: 'asc',
  },
  {
    id: 'title-desc',
    get label() {
      return i18n.t('projects:project.constants.nameZA');
    },
    sortBy: 'title',
    sortOrder: 'DESC',
    direction: 'desc',
  },
] as const satisfies ReadonlyArray<ProjectSortChoice>;

/**
 * `null` means "use the BE default" (created_at DESC) — the chip renders
 * inactive in that state and the SortChipPopover reset row labels it as
 * "Default". The FE intentionally starts here so the chip looks neutral on
 * first paint and the BE owns the canonical default.
 */
export const DEFAULT_PROJECT_SORT_ID: string | null = null;

export function resolveProjectSort(
  id: string | null,
): { sortBy: ProjectSortKey; sortOrder: 'ASC' | 'DESC' } | null {
  if (id === null) return null;
  const choice = PROJECT_SORT_CHOICES.find((c) => c.id === id);
  return choice ? { sortBy: choice.sortBy, sortOrder: choice.sortOrder } : null;
}

export const PROJECTS_COLUMNS: Column<ProjectListItem, ProjectSortKey>[] = [
  {
    key: 'title',
    get label() {
      return i18n.t('projects:project.constants.name');
    },
    sortable: true,
    className: 'w-[33%]',
  },
  {
    key: 'activity',
    get label() {
      return i18n.t('projects:project.constants.activity');
    },
    className: 'w-[22%]',
  },
  {
    key: 'status',
    get label() {
      return i18n.t('projects:project.constants.status');
    },
    sortable: true,
    filterable: 'multiple',
  },
  {
    key: 'owner',
    get label() {
      return i18n.t('projects:project.constants.owner');
    },
    filterable: 'single',
    className: 'w-[27%]',
  },
];

export const PROJECT_STATUS_MAP: Record<
  ProjectStatus,
  { label: string; variant: ProjectStatusVariant }
> = {
  draft: {
    get label() {
      return i18n.t('projects:project.constants.draft');
    },
    variant: 'neutral',
  },
  in_progress: {
    get label() {
      return i18n.t('projects:project.constants.inProgress');
    },
    variant: 'warning',
  },
  ready_to_review: {
    get label() {
      return i18n.t('projects:project.constants.readyToReview');
    },
    variant: 'info',
  },
  live: {
    get label() {
      return i18n.t('projects:project.constants.live');
    },
    variant: 'success',
  },
  signed_off: {
    get label() {
      return i18n.t('projects:project.constants.signedOff');
    },
    variant: 'success',
  },
};
